import { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import Lenis from "@studio-freight/lenis";
import { 
  Globe, 
  Target, 
  Palette, 
  Search, 
  Users, 
  TrendingUp, 
  MessageCircle,
  ChevronRight,
  Menu,
  X,
  Check,
  Send,
  Phone,
  Mail,
  Instagram,
  Linkedin,
  ArrowRight,
  Zap,
  BarChart3,
  Rocket
} from "lucide-react";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster, toast } from "sonner";

const API = "https://agencia-backend-zz9h.onrender.com/api";

// Animation hook for intersection observer
const useAnimateOnScroll = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const elements = document.querySelectorAll(".animate-fade-up, .animate-fade-in");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#servicos", label: "Serviços" },
    { href: "#processo", label: "Processo" },
    // { href: "#depoimentos", label: "Resultados" },
    { href: "#contato", label: "Contato" },
  ];

  return (
    <>
      <header
        data-testid="header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "glass border-b border-white/10" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#" className="flex items-center gap-2" data-testid="logo">
              <span className="text-2xl font-bold tracking-tight font-['Outfit']">
                TYPE<span className="text-[#B026FF]">.</span>
              </span>
            </a>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm text-zinc-400 hover:text-white transition-colors"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden md:block">
              <a
                href="#contato"
                className="bg-transparent border border-[#B026FF] text-white px-6 py-2.5 rounded-full hover:bg-[#B026FF] transition-all duration-300 btn-glow text-sm font-medium"
                data-testid="header-cta"
              >
                Análise Gratuita
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
        <button
          className="absolute top-6 right-6 text-white"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-menu-close"
        >
          <X size={24} />
        </button>
        {navLinks.map((link) => (
          <a
            key={link.href}
            href={link.href}
            className="text-2xl font-medium text-white hover:text-[#B026FF] transition-colors"
            onClick={() => setMobileMenuOpen(false)}
          >
            {link.label}
          </a>
        ))}
        <a
          href="#contato"
          className="mt-4 bg-[#B026FF] text-white px-8 py-3 rounded-full font-medium"
          onClick={() => setMobileMenuOpen(false)}
        >
          Análise Gratuita
        </a>
      </div>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
    >
      {/* Background */}
      <div
        className="hero-bg"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1764258559605-5b769b04550e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjAzZCUyMHdhdmUlMjBiYWNrZ3JvdW5kJTIwcHVycGxlJTIwbmVvbnxlbnwwfHx8fDE3NzQ3OTczOTl8MA&ixlib=rb-4.1.0&q=85')`,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <div className="animate-fade-up">
          <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-6">
            Agência Digital
          </span>
        </div>

        <h1 className="animate-fade-up stagger-1 text-4xl sm:text-5xl lg:text-7xl font-light tracking-tighter mb-6 font-['Outfit']">
          Transformamos pequenos negócios
          <br />
          em <span className="neon-text text-[#B026FF]">máquinas de vendas</span> 
        </h1>

        <p className="animate-fade-up stagger-2 text-base lg:text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
          Sites profissionais, design estratégico e anúncios que geram clientes de verdade. 
          Tudo o que seu negócio precisa para crescer na internet.
        </p>

        <div className="animate-fade-up stagger-3 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#contato"
            className="bg-transparent border border-[#B026FF] text-white px-8 py-4 rounded-full hover:bg-[#B026FF] transition-all duration-300 btn-glow font-medium flex items-center gap-2"
            data-testid="hero-cta-button"
          >
            Quero mais clientes
            <ChevronRight size={18} />
          </a>
          <a
            href="https://wa.me/32491137082"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#25D366] text-black font-semibold px-6 py-4 rounded-full hover:bg-[#20bd5a] flex items-center gap-2 transition-transform hover:scale-105"
            data-testid="hero-whatsapp-button"
          >
            <MessageCircle size={20} />
            Falar no WhatsApp
          </a>
        </div>

        {/* Stats */}
        <div className="animate-fade-up stagger-4 mt-20 grid grid-cols-3 gap-8 max-w-xl mx-auto">
          <div>
            <div className="text-3xl lg:text-4xl font-bold font-['Outfit'] text-white">50+</div>
            <div className="text-sm text-zinc-500">Projetos entregues</div>
          </div>
          <div>
            <div className="text-3xl lg:text-4xl font-bold font-['Outfit'] text-white">98%</div>
            <div className="text-sm text-zinc-500">Clientes satisfeitos</div>
          </div>
          <div>
            <div className="text-3xl lg:text-4xl font-bold font-['Outfit'] text-white">3x</div>
            <div className="text-sm text-zinc-500">Aumento médio em leads</div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Problems Section
const ProblemsSection = () => {
  const problems = [
    {
      icon: Search,
      title: "Seu negócio não aparece no Google?",
      description: "Seus concorrentes estão conquistando os clientes que deveriam ser seus."
    },
    {
      icon: Users,
      title: "Você depende só de indicação?",
      description: "Sem previsibilidade, seu faturamento fica refém da sorte."
    },
    {
      icon: TrendingUp,
      title: "Já tentou anúncios e não teve resultado?",
      description: "Sem estratégia, você só queima dinheiro e tempo."
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#050505]" data-testid="problems-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="animate-fade-up inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#B026FF] mb-4">
            O Problema
          </span>
          <h2 className="animate-fade-up stagger-1 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight font-['Outfit']">
            Você se identifica?
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {problems.map((problem, index) => (
            <div
              key={index}
              className={`animate-fade-up stagger-${index + 1} problem-card p-8 rounded-2xl bg-[#0F0F12] border border-white/10 card-hover`}
              data-testid={`problem-card-${index}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#B026FF]/10 flex items-center justify-center mb-6">
                <problem.icon className="text-[#B026FF]" size={24} />
              </div>
              <h3 className="text-xl font-medium mb-3 font-['Outfit']">{problem.title}</h3>
              <p className="text-zinc-400 leading-relaxed">{problem.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Globe,
      title: "Criação de Sites",
      description: "Sites rápidos, modernos e focados em conversão. Design responsivo que funciona em qualquer dispositivo.",
      features: ["Landing Pages", "Sites Institucionais", "E-commerce", "Blogs"]
    },
    {
      icon: Target,
      title: "Tráfego Pago",
      description: "Campanhas otimizadas para gerar clientes através do Google Ads e redes sociais.",
      features: ["Google Ads", "Meta Ads", "LinkedIn Ads", "Remarketing"]
    },
    {
      icon: Palette,
      title: "Design",
      description: "Identidade visual e criativos que chamam atenção e transmitem profissionalismo.",
      features: ["Logo & Identidade", "Social Media", "Apresentações", "Materiais"]
    }
  ];

  return (
    <section id="servicos" className="py-24 lg:py-32 bg-[#0A0A0A]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="animate-fade-up inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#B026FF] mb-4">
            Soluções
          </span>
          <h2 className="animate-fade-up stagger-1 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight font-['Outfit']">
            O que fazemos por você
          </h2>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className={`animate-fade-up stagger-${index + 1} group relative overflow-hidden rounded-2xl bg-[#0F0F12] border border-white/10 p-8 transition-all duration-300 hover:border-[#B026FF]/50 card-hover`}
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#B026FF] to-[#7B1FA2] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <service.icon className="text-white" size={28} />
              </div>
              <h3 className="text-2xl font-medium mb-3 font-['Outfit']">{service.title}</h3>
              <p className="text-zinc-400 leading-relaxed mb-6">{service.description}</p>
              <ul className="space-y-2">
                {service.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-zinc-500">
                    <Check size={14} className="text-[#B026FF]" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Ricardo Mendes",
      role: "CEO, TechStart",
      image: "https://images.unsplash.com/photo-1769636929231-3cd7f853d038?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhlYWRzaG90JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc0Nzk3MzkyfDA&ixlib=rb-4.1.0&q=85",
      text: "Em 3 meses triplicamos o número de leads qualificados. A Type entende de resultado.",
      result: "+200% em leads"
    },
    {
      name: "Ana Paula Costa",
      role: "Fundadora, Bella Estética",
      image: "https://images.unsplash.com/photo-1624670319970-37aa780d4874?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhlYWRzaG90JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc0Nzk3MzkyfDA&ixlib=rb-4.1.0&q=85",
      text: "O novo site e as campanhas mudaram completamente meu negócio. Agenda sempre cheia!",
      result: "Agenda lotada"
    },
    {
      name: "Carlos Eduardo",
      role: "Diretor, Construtora CE",
      image: "https://images.pexels.com/photos/262391/pexels-photo-262391.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      text: "Profissionais sérios que entregam o que prometem. Melhor investimento que fiz.",
      result: "+150% em vendas"
    }
  ];

 {/* return (
    <section id="depoimentos" className="py-24 lg:py-32 bg-[#050505]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        { <div className="text-center mb-16">
          <span className="animate-fade-up inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#B026FF] mb-4">
            Resultados
          </span>
          <h2 className="animate-fade-up stagger-1 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight font-['Outfit']">
            O que nossos clientes dizem
          </h2>
        </div> }

        { <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className={`animate-fade-up stagger-${index + 1} testimonial-card p-8 rounded-2xl bg-white/[0.02] border border-white/5`}
              data-testid={`testimonial-card-${index}`}
            >
              <div className="flex items-center gap-4 mb-6">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-14 h-14 rounded-full object-cover"
                />
                <div>
                  <div className="font-medium">{testimonial.name}</div>
                  <div className="text-sm text-zinc-500">{testimonial.role}</div>
                </div>
              </div>
              <p className="text-zinc-300 leading-relaxed mb-4">{testimonial.text}</p>
              <div className="inline-block px-3 py-1 rounded-full bg-[#B026FF]/10 text-[#B026FF] text-sm font-medium">
                {testimonial.result}
              </div>
            </div>
          ))}
        </div> }
      </div>
    </section>

  );
  */}
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      icon: Search,
      title: "Diagnóstico Gratuito",
      description: "Analisamos seu negócio, concorrentes e oportunidades de crescimento."
    },
    {
      number: "02",
      icon: Zap,
      title: "Estratégia Personalizada",
      description: "Criamos um plano de ação sob medida para seus objetivos."
    },
    {
      number: "03",
      icon: Rocket,
      title: "Execução",
      description: "Implementamos as soluções com agilidade e qualidade."
    },
    {
      number: "04",
      icon: BarChart3,
      title: "Crescimento",
      description: "Acompanhamos os resultados e otimizamos continuamente."
    }
  ];

  return (
    <section id="processo" className="py-24 lg:py-32 bg-[#0A0A0A]" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          <div>
            <span className="animate-fade-up inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#B026FF] mb-4">
              Processo
            </span>
            <h2 className="animate-fade-up stagger-1 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight font-['Outfit'] mb-6">
              Como trabalhamos
            </h2>
            <p className="animate-fade-up stagger-2 text-zinc-400 leading-relaxed mb-8">
              Um processo simples e transparente, focado em entregar resultados reais para o seu negócio.
            </p>
            
            <div className="space-y-6">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`animate-fade-up stagger-${index + 1} flex gap-4`}
                  data-testid={`process-step-${index}`}
                >
                  <div className="step-number flex-shrink-0">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-1 font-['Outfit']">{step.title}</h3>
                    <p className="text-zinc-400 text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* <div className="animate-fade-up relative">
            <img
              src=""
              alt="Workspace"
              className="rounded-2xl w-full object-cover"
            />
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
          </div> */}
        </div>
      </div>
    </section>
  );
};

// Contact/Offer Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        source: "website_contact_form"
      });
      setIsSuccess(true);
      toast.success("Mensagem enviada com sucesso! Entraremos em contato em breve.");
      setFormData({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contato" className="py-24 lg:py-32 bg-[#050505]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
          <div>
            <span className="animate-fade-up inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#B026FF] mb-4">
              Vamos conversar
            </span>
            <h2 className="animate-fade-up stagger-1 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight font-['Outfit'] mb-6">
              Pronto para dominar<br />seu mercado?
            </h2>
            <p className="animate-fade-up stagger-2 text-zinc-400 leading-relaxed mb-8">
              Solicite uma análise gratuita do seu negócio. Sem compromisso, sem enrolação. 
              Vamos mostrar exatamente como você pode crescer.
            </p>

            <div className="animate-fade-up stagger-3 space-y-4">
              <div className="flex items-center gap-3 text-zinc-400">
                <Check className="text-[#B026FF]" size={20} />
                <span>Análise completa do seu negócio</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-400">
                <Check className="text-[#B026FF]" size={20} />
                <span>Identificação de oportunidades</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-400">
                <Check className="text-[#B026FF]" size={20} />
                <span>Plano de ação personalizado</span>
              </div>
              <div className="flex items-center gap-3 text-zinc-400">
                <Check className="text-[#B026FF]" size={20} />
                <span>100% gratuito e sem compromisso</span>
              </div>
            </div>
          </div>

          <div className="animate-fade-up">
            {isSuccess ? (
              <div className="bg-[#0F0F12] border border-white/10 rounded-2xl p-8 text-center">
                <div className="success-check mx-auto mb-6">
                  <Check size={30} className="text-white" />
                </div>
                <h3 className="text-2xl font-medium mb-2 font-['Outfit']">Mensagem Enviada!</h3>
                <p className="text-zinc-400 mb-6">Entraremos em contato em até 24 horas.</p>
                <button
                  onClick={() => setIsSuccess(false)}
                  className="text-[#B026FF] hover:underline"
                >
                  Enviar outra mensagem
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-[#0F0F12] border border-white/10 rounded-2xl p-8">
                <div className="space-y-4">
                  <div>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Seu nome *"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-500 focus:border-[#B026FF] focus:ring-[#B026FF]"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Seu e-mail *"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-500 focus:border-[#B026FF] focus:ring-[#B026FF]"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Seu WhatsApp"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-500 focus:border-[#B026FF] focus:ring-[#B026FF]"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <Input
                      type="text"
                      name="company"
                      placeholder="Nome da empresa"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-500 focus:border-[#B026FF] focus:ring-[#B026FF]"
                      data-testid="contact-company-input"
                    />
                  </div>
                  <div>
                    <Textarea
                      name="message"
                      placeholder="Conte um pouco sobre seu negócio..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-500 focus:border-[#B026FF] focus:ring-[#B026FF] resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#B026FF] hover:bg-[#C955FF] text-white font-medium py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="contact-form-submit"
                  >
                    {isSubmitting ? (
                      <div className="spinner" />
                    ) : (
                      <>
                        Solicitar Análise Gratuita
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Differential Section
const DifferentialSection = () => {
  return (
    <section className="py-24 lg:py-32 bg-[#0A0A0A]" data-testid="differential-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="animate-fade-up relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#B026FF]/20 via-[#0F0F12] to-[#0F0F12] border border-[#B026FF]/30 p-8 lg:p-16">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#B026FF]/10 to-transparent" />
          
          <div className="relative z-10 max-w-2xl">
            <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#B026FF] mb-4">
              Exclusivo
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight font-['Outfit'] mb-6">
              Veja como seu negócio<br />poderia ser
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-8">
              Envie seu site atual ou Instagram e receba uma análise personalizada 
              com sugestões de melhoria. Sem custo, sem compromisso.
            </p>
            <a
              href="#contato"
              className="inline-flex items-center gap-2 bg-[#B026FF] hover:bg-[#C955FF] text-white font-medium px-8 py-4 rounded-full transition-all duration-300"
              data-testid="differential-cta"
            >
              Quero ver meu potencial
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-16 lg:py-24 bg-[#050505] border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="md:col-span-2">
            <span className="text-3xl font-bold tracking-tight font-['Outfit'] mb-4 block">
              TYPE<span className="text-[#B026FF]">.</span>
            </span>
            <p className="text-zinc-500 max-w-sm mb-6">
              Transformando negócios em resultados. Sua presença digital nas mãos certas.
            </p>
            <div className="flex gap-4">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#B026FF] transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-[#B026FF] transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-medium mb-4 font-['Outfit']">Links</h4>
            <ul className="space-y-3">
              <li><a href="#servicos" className="text-zinc-500 hover:text-white transition-colors">Serviços</a></li>
              <li><a href="#processo" className="text-zinc-500 hover:text-white transition-colors">Processo</a></li>
              <li><a href="#depoimentos" className="text-zinc-500 hover:text-white transition-colors">Resultados</a></li>
              <li><a href="#contato" className="text-zinc-500 hover:text-white transition-colors">Contato</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-medium mb-4 font-['Outfit']">Contato</h4>
            <ul className="space-y-3">
              <li>
                <a href="mailto:sac@alphatype.store" className="text-zinc-500 hover:text-white transition-colors flex items-center gap-2">
                  <Mail size={16} />
                  sac@alphatype.store
                </a>
              </li>
              <li>
                <a href="https://wa.me/qr/WLHSJN4HCST6F1" className="text-zinc-500 hover:text-white transition-colors flex items-center gap-2">
                  <Phone size={16} />
                  (32) 49113-7082
                </a>
              </li>
            </ul> 
          </div>
        </div>

        {/* Watermark */}
        <div className="text-center overflow-hidden">
          <div className="footer-watermark font-['Outfit']">TYPE AGÊNCIA</div>
        </div>

        {/* Copyright */}
        <div className="text-center mt-8 pt-8 border-t border-white/5">
          <p className="text-zinc-600 text-sm">
            © {new Date().getFullYear()} Type Agência. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Float Button
const WhatsAppFloat = () => {
  return (
    <a
      href="https://wa.me/32491137082"
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:bg-[#20bd5a] transition-colors"
      data-testid="whatsapp-float"
    >
      <MessageCircle size={28} />
    </a>
  );
};

// Main App Component
function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: "vertical",
      gestureDirection: "vertical",
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenisRef.current?.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenisRef.current?.destroy();
    };
  }, []);

  useAnimateOnScroll();

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <Header />
      <main>
        <HeroSection />
        <ProblemsSection />
        <ServicesSection />
        <TestimonialsSection />
        <ProcessSection />
        <DifferentialSection />
        <ContactSection />
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

export default App;
